import { supabase } from '../lib/supabase';

// Create property inquiry with enhanced data structure
export const createPropertyInquiry = async (inquiryData) => {
  try {
    // Ensure we have the required message field
    const enhancedData = {
      message: inquiryData?.message || `Demande de ${inquiryData?.type || 'renseignements'} pour la propriété ${inquiryData?.property || ''}`,
      property_id: inquiryData?.propertyId || null,
      client_phone: inquiryData?.phone || null,
      preferred_contact_time: inquiryData?.preferred_contact_time || null,
      status: 'new',
      client_id: null, // Anonymous request initially
      created_at: new Date()?.toISOString()
    };

    const { data, error } = await supabase
      ?.from('property_inquiries')
      ?.insert([enhancedData])
      ?.select()
      ?.single();

    if (error) {
      return { data: null, error: error?.message };
    }

    return { data, error: null };
  } catch (error) {
    if (error?.message?.includes('Failed to fetch')) {
      return { 
        data: null, 
        error: 'Cannot connect to database. Your Supabase project may be paused or inactive.' 
      };
    }
    return { data: null, error: 'Failed to create property inquiry' };
  }
};

// Create quick request (visite or info) with proper data structure
export const createQuickRequest = async (requestType, userInfo = {}, propertyInfo = {}) => {
  try {
    const requestData = {
      message: requestType === 'visite'
        ? `Demande de visite rapide pour la propriété: ${propertyInfo?.title || 'Propriété non spécifiée'}`
        : `Demande d'informations pour la propriété: ${propertyInfo?.title || 'Propriété non spécifiée'}`,
      status: 'new',
      client_phone: userInfo?.phone || null,
      client_email: userInfo?.email || null,
      preferred_contact_time: userInfo?.preferred_contact_time || null,
      property_id: propertyInfo?.propertyId || null,
      client_id: null, // Anonymous request initially
      created_at: new Date()?.toISOString()
    };

    const { data, error } = await supabase
      ?.from('property_inquiries')
      ?.insert([requestData])
      ?.select()
      ?.single();

    if (error) {
      return { data: null, error: error?.message };
    }

    return { data, error: null };
  } catch (error) {
    if (error?.message?.includes('Failed to fetch')) {
      return {
        data: null,
        error: 'Cannot connect to database. Your Supabase project may be paused or inactive.'
      };
    }
    return { data: null, error: 'Failed to create quick request' };
  }
};

// Create property inquiry from button click data
export const createPropertyInquiryFromClick = async (clickData) => {
  try {
    const inquiryTypes = {
      'info': 'Demande d\'informations générales',
      'viewing': 'Demande de visite programmée',
      'investment': 'Demande d\'analyse d\'investissement', 
      'legal': 'Demande de consultation légale'
    };

    const requestData = {
      message: `${inquiryTypes?.[clickData?.type] || 'Demande de renseignements'} pour la propriété: ${clickData?.property || 'Propriété'}`,
      property_id: clickData?.propertyId || null,
      status: 'new',
      client_phone: null,
      preferred_contact_time: null,
      client_id: null, // Anonymous request
      created_at: new Date()?.toISOString()
    };

    const { data, error } = await supabase
      ?.from('property_inquiries')
      ?.insert([requestData])
      ?.select()
      ?.single();

    if (error) {
      return { data: null, error: error?.message };
    }

    return { data, error: null };
  } catch (error) {
    if (error?.message?.includes('Failed to fetch')) {
      return { 
        data: null, 
        error: 'Cannot connect to database. Your Supabase project may be paused or inactive.' 
      };
    }
    return { data: null, error: 'Failed to create property inquiry from click' };
  }
};

// Get all property inquiries
export const getPropertyInquiries = async () => {
  try {
    const { data, error } = await supabase?.from('property_inquiries')?.select(`
        *,
        client:user_profiles!client_id(id, full_name, email, phone),
        property:properties(id, title, address, property_type)
      `)?.order('created_at', { ascending: false });

    if (error) {
      return { data: [], error: error?.message };
    }

    return { data: data || [], error: null };
  } catch (error) {
    if (error?.message?.includes('Failed to fetch')) {
      return { 
        data: [], 
        error: 'Cannot connect to database. Your Supabase project may be paused or inactive.' 
      };
    }
    return { data: [], error: 'Failed to fetch property inquiries' };
  }
};

// Update property inquiry
export const updatePropertyInquiry = async (id, updates) => {
  try {
    const { data, error } = await supabase?.from('property_inquiries')?.update(updates)?.eq('id', id)?.select()?.single();

    if (error) {
      return { data: null, error: error?.message };
    }

    return { data, error: null };
  } catch (error) {
    if (error?.message?.includes('Failed to fetch')) {
      return { 
        data: null, 
        error: 'Cannot connect to database. Your Supabase project may be paused or inactive.' 
      };
    }
    return { data: null, error: 'Failed to update property inquiry' };
  }
};

// Delete property inquiry
export const deletePropertyInquiry = async (id) => {
  try {
    const { error } = await supabase?.from('property_inquiries')?.delete()?.eq('id', id);

    if (error) {
      return { error: error?.message };
    }

    return { error: null };
  } catch (error) {
    if (error?.message?.includes('Failed to fetch')) {
      return { 
        error: 'Cannot connect to database. Your Supabase project may be paused or inactive.' 
      };
    }
    return { error: 'Failed to delete property inquiry' };
  }
};

export default {
  createPropertyInquiry,
  createQuickRequest,
  createPropertyInquiryFromClick,
  getPropertyInquiries,
  updatePropertyInquiry,
  deletePropertyInquiry
};